import { VIEW_CATEGORIES } from '@/constants/views'

/*
 * Docs navigation. Groups follow the Figma file: Get started, Foundations, Components, Charts,
 * Blocks and Templates. The order here drives the sidebar, search and the previous and next links.
 */
export interface DocsNavItem {
    title: string
    href: string
    description: string
    figma?: string
}

export interface DocsNavGroup {
    title: string
    items: DocsNavItem[]
}

export const COMPONENT_NAV: DocsNavItem[] = [
    { title: 'Accordion', href: '/docs/components/accordion', description: 'The source accordion: SourceList, Source, Group and DetailRow with count badges.', figma: '11 / Components / Accordion' },
    { title: 'Avatar', href: '/docs/components/avatar', description: 'People and organizations, with a neutral placeholder and overlapping groups.', figma: '10 / Components / Avatar' },
    { title: 'Badge', href: '/docs/components/badge', description: 'Status, labels and counts, including the GoodUnited status set and score badge.', figma: '10 / Components / Badge' },
    { title: 'Button', href: '/docs/components/button', description: 'Actions in five variants and six sizes.', figma: '10 / Components / Button' },
    { title: 'Card', href: '/docs/components/card', description: 'A raised container with a header, content and footer.', figma: '10 / Components / Card' },
    { title: 'Chat', href: '/docs/components/chat', description: 'Supporter Response: contact rows, tags, message bubbles, timeline events and the reply composer.', figma: '10 / Components / Chat' },
    { title: 'Checkbox', href: '/docs/components/checkbox', description: 'Select one or more independent options.', figma: '10 / Components / Checkbox' },
    { title: 'ChecklistItem', href: '/docs/components/checklist-item', description: 'Complete, incomplete and info marks beside a line of copy.', figma: '10 / Components / ChecklistItem' },
    { title: 'DatePicker', href: '/docs/components/date-picker', description: 'Single dates, day ranges and month ranges with presets.', figma: '10 / Components / DatePicker' },
    { title: 'Dialog', href: '/docs/components/dialog', description: 'Focused tasks and confirmations: Dialog and AlertDialog.', figma: '10 / Components / Dialog' },
    { title: 'DropdownMenu', href: '/docs/components/dropdown-menu', description: 'A list of actions or options opened from a trigger.', figma: '10 / Components / DropdownMenu' },
    { title: 'Field', href: '/docs/components/field', description: 'A label, a control and helper or error text, stacked or in a row.', figma: '10 / Components / Field' },
    { title: 'Input', href: '/docs/components/input', description: 'Single-line text entry, including the search input group.', figma: '10 / Components / Input' },
    { title: 'Label', href: '/docs/components/label', description: 'Form labels and inline labels with an icon.', figma: '10 / Components / Label' },
    { title: 'Metrics', href: '/docs/components/metrics', description: 'Metric cards, values, summaries and count badges.', figma: '10 / Components / Metrics' },
    { title: 'NotificationIndicator', href: '/docs/components/notification-indicator', description: 'A small ring that marks new activity.', figma: '10 / Components / NotificationIndicator' },
    { title: 'Popover', href: '/docs/components/popover', description: 'Rich content, such as filters, anchored to a trigger.', figma: '10 / Components / Popover' },
    { title: 'PostEngagement', href: '/docs/components/post-engagement', description: 'Views, likes and comments for a social post.', figma: '10 / Components / PostEngagement' },
    { title: 'RadioGroup', href: '/docs/components/radio-group', description: 'Choose exactly one option from a short list.', figma: '10 / Components / RadioGroup' },
    { title: 'Select', href: '/docs/components/select', description: 'Choose one value from a list in a compact trigger.', figma: '10 / Components / Select' },
    { title: 'Separator', href: '/docs/components/separator', description: 'A 1px rule between groups of content.', figma: '10 / Components / Separator' },
    { title: 'Sidebar', href: '/docs/components/sidebar', description: 'Navigation buttons, sub buttons and actions for the app sidebar.', figma: '10 / Components / Sidebar' },
    { title: 'Switch', href: '/docs/components/switch', description: 'Turn a single setting on or off immediately.', figma: '10 / Components / Switch' },
    { title: 'Table', href: '/docs/components/table', description: 'Rows of records with sortable headers, cells, pagination and link cards.', figma: '10 / Components / Table' },
    { title: 'Tabs', href: '/docs/components/tabs', description: 'Segmented tabs and icon tabs for switching views.', figma: '10 / Components / Tabs' },
    { title: 'Textarea', href: '/docs/components/textarea', description: 'Multi-line text entry that grows with its content.', figma: '10 / Components / Textarea' },
    { title: 'Tooltip', href: '/docs/components/tooltip', description: 'A short hint on hover or focus.', figma: '10 / Components / Tooltip' },
    { title: 'TreeToggle', href: '/docs/components/tree-toggle', description: 'Expand and collapse a row in a tree.', figma: '10 / Components / TreeToggle' },
    { title: 'WritingStyle', href: '/docs/components/writing-style', description: 'Section cards, the upload zone, resource rows and the navigation of the writing-style settings.', figma: '10 / Components / Chat' }
]

export const DOCS_NAV: DocsNavGroup[] = [
    {
        title: 'Get started',
        items: [
            { title: 'Introduction', href: '/docs', description: 'What the GoodUnited design system is and how it is organized.' }
        ]
    },
    {
        title: 'Foundations',
        items: [
            { title: 'Semantic colors', href: '/docs/foundations/semantic-colors', description: 'Every semantic color token in light and dark.', figma: '01 / Foundations / Semantic colors' },
            { title: 'Primitives', href: '/docs/foundations/primitives', description: 'The raw color scales, radius and effects.', figma: '02 / Foundations / Primitives' },
            { title: 'Typography', href: '/docs/foundations/typography', description: 'Poppins and Roboto, the twelve text styles and when to use each size.', figma: '03 / Foundations / Typography' },
            { title: 'Icons', href: '/docs/foundations/icons', description: 'Phosphor regular plus the GoodUnited navigation and product icons from Figma.', figma: '91 / Assets / Icons' }
        ]
    },
    {
        title: 'Components',
        items: COMPONENT_NAV
    },
    {
        title: 'Charts',
        items: [
            { title: 'Charts', href: '/docs/charts', description: 'The shared chart anatomy and the area, line, bar, stacked and sparkline plots.', figma: '12 / Charts' }
        ]
    },
    {
        title: 'Blocks',
        items: VIEW_CATEGORIES.filter(category => category.group === 'blocks').map(category => ({
            title: category.title,
            href: `/docs/blocks/${category.slug}`,
            description: category.description,
            figma: category.figma
        }))
    },
    {
        title: 'Templates',
        items: VIEW_CATEGORIES.filter(category => category.group === 'templates').map(category => ({
            title: category.title,
            href: `/docs/templates/${category.slug}`,
            description: category.description,
            figma: category.figma
        }))
    }
]

export const TOP_NAV = [
    { title: 'Docs', href: '/docs', match: ['/docs', '/docs/foundations'] },
    { title: 'Components', href: '/docs/components', match: ['/docs/components'] },
    { title: 'Charts', href: '/docs/charts', match: ['/docs/charts'] },
    { title: 'Blocks', href: '/docs/blocks/app-layout', match: ['/docs/blocks'] },
    { title: 'Templates', href: '/docs/templates/page-layouts', match: ['/docs/templates'] },
    { title: 'Settings', href: '/settings', match: ['/settings'] }
]

/*
 * Demo pages: full screens built only from the design system, linked to each other through the app sidebar.
 * section is the sidebar group a demo belongs to, and picks its icon in the docs header.
 */
export type DemoSection = 'insights' | 'actions' | 'acquire' | 'grow' | 'supporters' | 'settings'

export const DEMO_NAV: { title: string, description: string, href: string, section: DemoSection }[] = [
    { title: 'Insights', description: 'Who your supporters are, this week and follow-ups, plus revenue and subscriber acquisition', href: '/demo/insights', section: 'insights' },
    { title: 'Action Inbox', description: 'AI-recommended actions to review, approve and follow to launch', href: '/demo/inbox', section: 'actions' },
    { title: 'Outcomes', description: 'Your outcomes and the outcome library that drive the inbox', href: '/demo/outcomes', section: 'actions' },
    { title: 'Acquire tools', description: 'Journey URLs and Facebook page replies with their supporters', href: '/demo/acquire', section: 'acquire' },
    { title: 'Custom audiences', description: 'Contact lists matched to Messenger subscribers, with the create flow', href: '/demo/custom-audiences', section: 'acquire' },
    { title: 'Grow', description: 'Fundraiser and donation pages with revenue, sources and the create flow', href: '/demo/grow', section: 'grow' },
    { title: 'Supporters', description: 'Filters, segments, lists, recommendations and supporter profiles', href: '/demo/supporters', section: 'supporters' },
    { title: 'Supporter Response', description: 'Reply to the stories supporters shared, with AI drafted replies', href: '/demo/supporter-response', section: 'supporters' },
    { title: 'AI Context', description: 'The background and pages the AI uses to write in your voice', href: '/demo/ai-context', section: 'settings' },
    { title: 'Admin settings', description: 'Account, segments, tags, fields, default reply, team and integrations', href: '/demo/admin', section: 'settings' }
]

export const FLAT_DOCS: (DocsNavItem & { group: string })[] = DOCS_NAV.flatMap(group => group.items.map(item => ({ ...item, group: group.title })))

export const getNeighbours = (href: string) => {
    const index = FLAT_DOCS.findIndex(item => item.href === href)

    return {
        previous: index > 0 ? FLAT_DOCS[index - 1] : null,
        next: index >= 0 && index < FLAT_DOCS.length - 1 ? FLAT_DOCS[index + 1] : null
    }
}
